import { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { LayoutDashboard, FileText, Send, BarChart2, Users, MoreHorizontal,
         Settings, Globe, Landmark, LogOut, ChevronDown, Check, X } from 'lucide-react';
import { settingsApi, revolutApi, companyApi, JUST_SWITCHED_KEY } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useStepUp } from '../contexts/StepUpContext';
import { useToast } from './Toast';
import { Badge, Truncate, Modal, Btn } from './UI';
import FeedbackWidget from './FeedbackWidget';
import ChatWidget from './ChatWidget';
import { useAuthedObjectUrl } from './AuthedFile';

// A `Membership.role` a backendben `admin` | `co_worker` (lowercase value a
// JSON-ban — `models/tenant.py::MembershipRole`). Tűrő összehasonlítás, a
// `.claude/rules/db-enums-migracio.md` „legyen tűrő" elve szerint.
const roleLabel = (role) => String(role || '').toLowerCase() === 'admin' ? 'ADMIN' : 'MUNKATÁRS';

const ENV_RED = '#ef4444', ENV_GREEN = '#22c55e';

const SETTINGS_SUBNAV = [
  { label: 'Cég beállítások', to: '/settings/company' },
  // ── M11 (2026-09-05): ÖT típusonkénti asset-menüpont, a Cég beállítások UTÁN, a
  // nem-asset pontok ELŐTT (Gábor döntése: nem egy összevont „Kapcsolatok" fül).
  // A sorrend a MEGJELENÍTÉSI sorrend (nem fejlesztési ütemezés). A `Számlázó
  // programok` és a `NAV Online Számla` ma üres-állapotú (M7/M10-ig).
  { label: 'NAV Online Számla', to: '/settings/nav-osa' },
  { label: 'Bankszámlák',       to: '/settings/banks' },
  { label: 'Számlázó programok', to: '/settings/invoicing' },
  // ⚠ Adófolyószámla (NAV ÜPO) ≠ Online Számla. Route VÁLTOZATLAN (/settings/nav-upo),
  // csak a címke lett „Adófolyószámlák" (típusonkénti menü, D3 1.7).
  { label: 'Adófolyószámlák',   to: '/settings/nav-upo' },
  { label: 'E-mail fiókok',     to: '/settings/email' },
  { label: 'Kategóriák',      to: '/settings/categories' },
  { label: 'Partnerek',       to: '/settings/partners' },
  { label: 'Projektek',       to: '/settings/projects' },
  // Tulajdonosi döntés 2026-09-29: a Munkavállalók modul MINDEN része csak cég-adminisztrátornak
  // (a backend is 403-at ad) — nem-adminnak a menüpont meg sem jelenik.
  { label: 'Munkavállalók',   to: '/settings/employees', adminOnly: true },
  { label: 'Biztonság',       to: '/settings/security' },
  // D-WP-08: csak az "első fül" (B3 döntés) — a Dashboard-chatnek nincs
  // Telegram-kapcsolódási alfüle, az a Telegram-terv (ma ⏸ elhalasztott) dolga.
  { label: 'Chat',            to: '/settings/chat' },
];

// „Információk" — a korábbi „Statisztikák" menüpont új neve és szerkezete
// (Gábor döntése, 2026-08-04). Ok: a NAV-egyeztetés nem statisztika, hanem
// teendő-lista, viszont nem is a Bizonylatok oldalra való.
//
// ⚠ A Statisztikák oldal BELSŐ fül-szerkezete (ma 6 fül, és nő) külön
// újrastrukturálásra vár, a Beállítások mintájára — az önálló feladat, lásd
// `wiki/TOVABBFEJLESZTESEK.md`. Most csak a menü-szint kapott subnavot, hogy az
// egyeztetés helyet találjon a meglévő oldal érintése nélkül.
const INFO_SUBNAV = [
  { label: 'Statisztikák',          to: '/stats' },
  /* Két KÜLÖN menüpont, szándékosan: az „adófolyószámla" a NAV-tól kapott adat
     MEGTEKINTÉSE, az „egyeztetés" a könyvelői adattal való ÖSSZEVETÉS — az
     utóbbi csak az ELTÉRÉSEKET mutatja, tehát nem helyettesíti az előzőt. */
  { label: 'NAV adófolyószámla', to: '/info/nav-adofolyoszamla' },
  { label: 'NAV egyeztetés',      to: '/info/nav-egyeztetes' },
];

// ⚠⚠ A `mobile: true` EXPLICIT jelölés, nem pozíció (2026-09-16, Partnerek modul F1).
// Korábban a mobil sáv `NAV.slice(0, 5)` volt — vagyis egy ÚJ menüpont beszúrása
// CSENDBEN kilökte volna az ötödiket (az Információkat), anélkül hogy bárki döntött
// volna róla. Ez a fail-silent osztály, amit a CLAUDE.md 4. fejezete tilt.
const NAV = [
  { label: 'Dashboard',     to: '/',            icon: LayoutDashboard, mobile: true },
  { label: 'Bizonylatok',   to: '/documents',   icon: FileText,        mobile: true },
  // A partner a bizonylat MÁSIK oldala — ezért a Bizonylatok szomszédja, nem a menü alja.
  { label: 'Partnerek',     to: '/partners',    icon: Users, match: ['/partners'] },
  { label: 'Kivonatok',     to: '/statements',  icon: Landmark,        mobile: true },
  { label: 'Könyvelő',      to: '/accountant',  icon: Send,            mobile: true },
  { label: 'Információk',   to: '/stats',       icon: BarChart2, sub: INFO_SUBNAV,
    match: ['/stats', '/info'] },
  { label: 'Domének',       to: '/domains',     icon: Globe },
  { label: 'Beállítások',   to: '/settings/company', icon: Settings, sub: SETTINGS_SUBNAV,
    match: ['/settings'] },
];

// K3 (2026-09-16, tulajdonosi döntés): „Mobilon a »Több« menüben legyen."
// A mobil alsó sáv = 4 napi-cselekvés menüpont + egy „Több" gomb, ami alsó lapon
// nyitja a MARADÉK menüpontokat. Így nincs 5-elem korlát és nincs önkényes áldozat:
// ami nem fér a sávba, az nem tűnik el, hanem egy kattintással elérhető.
const MOBILE_BAR = NAV.filter(i => i.mobile);
const MOBILE_MORE = NAV.filter(i => !i.mobile);

/* Egy menüpont + (ha van) az alcsoportja. Kiemelve, mert két helyen kellene
 * ugyanez a stílus-logika, és a subnav megjelenítése már nem egyetlen útvonal-
 * prefixhez (`/settings`) van kötve, hanem a menüpont saját `match` listájához. */
function NavGroup({ item, open }) {
  const { label, to, icon: Icon } = item;
  const { isAdmin } = useAuth();
  const sub = item.sub && item.sub.filter(s => !s.adminOnly || isAdmin);
  return (
    <div>
      <NavLink to={to} end={to === '/'} style={({ isActive }) => {
        const on = isActive || open;
        return {
          display:'flex', alignItems:'center', gap:9,
          padding:'0.5rem 0.7rem', borderRadius:'var(--radius)',
          marginBottom:2, fontSize:'0.8rem', fontWeight:500,
          color: on ? '#fff' : 'var(--text-muted)',
          background: on ? 'var(--primary-subtle, rgba(59,130,246,0.15))' : 'transparent',
          borderLeft: `2px solid ${on ? 'var(--brand, var(--primary))' : 'transparent'}`,
          textDecoration:'none', transition:'all 0.12s',
        };
      }}>
        <Icon size={14}/>
        {label}
      </NavLink>
      {sub && open && (
        <div style={{marginBottom:4}}>
          {sub.map(s => (
            <NavLink key={s.to} to={s.to} end style={({ isActive }) => ({
              display:'block', padding:'0.4rem 0.7rem 0.4rem 2.1rem',
              borderRadius:'var(--radius)', marginBottom:1, fontSize:'0.78rem',
              fontWeight: isActive ? 600 : 500,
              color: isActive ? '#fff' : 'var(--text-muted)',
              background: isActive ? 'var(--primary-subtle, rgba(59,130,246,0.15))' : 'transparent',
              borderLeft: `2px solid ${isActive ? 'var(--brand, var(--primary))' : 'transparent'}`,
              textDecoration:'none', transition:'all 0.12s',
            })}>
              {s.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout, memberships, activeCompanyId, switchCompany } = useAuth();
  // M1 (2026-09-04): utalás közben / nyitott step-up alatt a cégváltó tiltott.
  const { stepUpOpen, paymentInFlight } = useStepUp();
  const toast = useToast();
  const { data: settings = {} } = useQuery({
    queryKey: ['settings'],
    queryFn: settingsApi.get,
  });
  // Cégnév: a `companies` tábla (a logó viszont settings — az UI-állapot).
  const { data: company = {} } = useQuery({
    queryKey: ['company'],
    queryFn: companyApi.get,
    staleTime: 5 * 60 * 1000,
  });
  const { data: revEnvStatus = {} } = useQuery({
    queryKey: ['revolut-env-status'],
    queryFn: revolutApi.envStatus,
    refetchInterval: 30000,
  });
  const revEnv = revEnvStatus.environment;          // 'sandbox' | 'production' | undefined (töltés)
  // ⚠ 2026-09-28 (Gábor): az „ÉLES REVOLUT" sáv és jelvény CSAK akkor, ha a cégnek van éles
  //   Revolut API-kapcsolata. Egy élesként létrehozott cégen kulcs nélkül a sáv hamis riasztás
  //   volt (a cég-szintű `is_sandbox=false` önmagában nem jelent Revolut-kapcsolatot).
  const revLive = revEnv === 'production' && !!revEnvStatus.production_configured;
  const revConnected = revEnv === 'sandbox' || revLive;
  const location = useLocation();
  // Egy csoport (subnavos menüpont) akkor „nyitott", ha az aktuális útvonal a
  // saját prefixei közé esik. Korábban ez `pathname.startsWith('/settings')` volt
  // — azzal a Beállításokon KÍVÜL egyetlen subnav sem tudott megjelenni, tehát az
  // „Információk" alcsoportja láthatatlan maradt volna.
  const inGroup = (item) =>
    (item.match || [item.to]).some(p => location.pathname.startsWith(p));
  // A mobil „Több" alsó lapja (K3). Útvonal-váltásra záródik — enélkül egy
  // háttérben maradt lap eltakarná az épp megnyitott oldalt.
  const [moreOpen, setMoreOpen] = useState(false);
  useEffect(() => { setMoreOpen(false); }, [location.pathname]);
  // A cégnév a `companies` táblából (a `settings.company_name` kulcs 2026-08-12-én
  // megszűnt — ugyanaz az adat két helyen élt). A logó MARAD settings-alapú.
  const companyName = company.name || 'Cég';
  const logoVersion = settings.company_logo_updated || null;
  // ⚠ 2026-09-28: a logó címe CÉGENKÉNT különbözik (`c=`) — a cégmásolás a verziószámot is
  //   átviszi, és azonos cím mellett egy másik cégnél elbukott betöltés (`logoErr`) vagy a
  //   böngésző gyorsítótára rejtette el / keverte a logót cégváltás után.
  const logoKey = `${activeCompanyId || ''}:${logoVersion || ''}`;
  // Skin alkalmazása a DB-beli ui_skin setting alapján (Beállítások → Megjelenítés)
  const uiSkin = settings.ui_skin === 'raymond' ? 'raymond' : null;
  useEffect(() => {
    if (uiSkin) document.documentElement.setAttribute('data-skin', uiSkin);
    else document.documentElement.removeAttribute('data-skin');
  }, [uiSkin]);
  const [logoErr, setLogoErr] = useState(false);
  // Új logó feltöltése után (version vált) próbáljuk újra a betöltést
  useEffect(() => { setLogoErr(false); }, [logoKey]);
  // ⚠ Blobként, az aktív cég nevében: egy <img src="/api/..."> nem vinné a cég-fejlécet,
  //   és a backend a legrégebbi tagság cégének logóját adná (a `c=` csak a címet
  //   különbözteti meg, a cégválasztást NEM) — components/AuthedFile.js.
  const logo = useAuthedObjectUrl(logoVersion
    ? `/api/settings/logo?c=${activeCompanyId || ''}&v=${logoVersion}` : null);
  const showLogo = !!logoVersion && !logoErr && !logo.error && !!logo.src;

  // Fiók-lenyíló a felső sávban (cég-logó + user), kattintásra nyit, kívülre
  // kattintva zár.
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);
  const userInitial = (user?.email || '?')[0].toUpperCase();

  // ── Cégváltó (M1, 2026-09-04 — wiki/proposals/multi-tenant-company-switcher.md) ──
  // CSAK `memberships.length > 1` esetén jelenik meg; egy cégnél a fejléc
  // PIXELRE ugyanaz, mint eddig (a cég-logó/monogram a fiók-gombban marad).
  // Több cégnél a logó/monogram ÁTKÖLTÖZIK az új switcher-gombba, a fiók-gomb
  // csak az avatart + chevront tartja — a lenyíló tartalma változatlan.
  const multiCompany = (memberships || []).length > 1;
  const switcherDisabled = paymentInFlight || stepUpOpen;
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const switcherRef = useRef(null);
  // M12 (2026-09-05): „lejárt" = companies.is_active=false VAGY
  // subscription_expires_at < now() (VAGY-kapcsolat, Gábor döntése) — a
  // backend `/auth/me` számolja (`expired`), a lista csak olvassa. Lejárt cégre
  // nem lehet váltani; az „Aktiválás" a FeedbackWidgetre mutat (nincs
  // önkiszolgáló fizetési flow — az admin-oldali aktiválás a stopgap).
  const liveMemberships = (memberships || []).filter(m => !m.expired);
  const expiredMemberships = (memberships || []).filter(m => m.expired);
  const [activateFor, setActivateFor] = useState(null);   // membership | null
  const openFeedbackFor = (m) => {
    setActivateFor(null);
    window.dispatchEvent(new CustomEvent('pf:open-feedback', { detail: {
      kind: 'QUESTION',
      title: `Előfizetés aktiválása: ${m.company_name}`,
      body: 'Szeretném aktiválni / megújítani a cég előfizetését.',
    }}));
  };
  useEffect(() => {
    if (!switcherOpen) return;
    const onClick = (e) => {
      if (switcherRef.current && !switcherRef.current.contains(e.target)) setSwitcherOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [switcherOpen]);
  // Váltás utáni visszajelzés: a váltás teljes újratöltéssel jár, ezért a toast
  // az ÚJ oldalon, egyszer, a sessionStorage-flagből jön. Ha a backend MÁS céget
  // oldott fel, mint amire a user kattintott (verseny: a tagság időközben
  // visszavonva), hibát mondunk, nem sikert.
  const switchedHandled = useRef(false);
  useEffect(() => {
    if (switchedHandled.current) return;
    let raw = null;
    try { raw = sessionStorage.getItem(JUST_SWITCHED_KEY); sessionStorage.removeItem(JUST_SWITCHED_KEY); }
    catch { return; }
    if (!raw) return;
    switchedHandled.current = true;
    let info = null;
    try { info = JSON.parse(raw); } catch { return; }
    if (info && info.id === activeCompanyId) {
      toast.success('Aktív cég: ' + (info.name || 'kiválasztva'));
    } else {
      toast.error('Nem sikerült erre a cégre váltani — nincs hozzá jogosultságod.');
    }
  }, [activeCompanyId, toast]);

  const companyBadge = showLogo ? (
    // A feltöltött cég-logó SAJÁT (tetszőleges színű/hátterű) képfájl —
    // nem kap alá se hátteret, se dobozt, ahogy a felhasználó kérte.
    // Feltétel: aki dolgot vezet a puo-ba, sötét háttérhez illő (pl.
    // fehér/világos) logót töltsön fel — ez a Beállítások oldalon
    // jelezhető, de itt kódban nem kényszeríthető ki.
    <img src={logo.src} alt={companyName}
      onError={()=>setLogoErr(true)}
      style={{height:26, width:'auto', maxWidth:130, objectFit:'contain', display:'block'}}/>
  ) : (
    <span style={{height:26, minWidth:26, padding:'0 7px', borderRadius:7,
      background:'var(--bg-hover)', border:'1px solid var(--border)',
      display:'flex', alignItems:'center', justifyContent:'center',
      fontSize:'0.68rem', fontWeight:700, color:'var(--text-muted)'}}>
      {companyName.slice(0,2).toUpperCase()}
    </span>
  );

  // Drop-célpont mellé ejtett fájlnál a böngésző alapból elnavigál és megnyitja
  // a PDF-et – ezt globálisan letiltjuk (a célzott drop-kezelők ettől még futnak).
  useEffect(() => {
    const prevent = (e) => e.preventDefault();
    window.addEventListener('dragover', prevent);
    window.addEventListener('drop', prevent);
    return () => {
      window.removeEventListener('dragover', prevent);
      window.removeEventListener('drop', prevent);
    };
  }, []);

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100vh',overflow:'hidden'}}>
      {/* Felso sav: puo-marka (Raymond skinben "Raymond" felirat) + cég-logó +
          felhasználói fiók-lenyíló. */}
      <header style={{height:56, flexShrink:0, background:'var(--bg-surface)',
        borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center',
        padding:'0 18px', gap:16}}>
        <NavLink to="/" style={{display:'flex', textDecoration:'none'}}>
          {uiSkin === 'raymond' ? (
            <span style={{fontFamily:'var(--font-logo)', fontStyle:'italic', fontWeight:800,
              fontSize:28, lineHeight:1, color:'var(--brand)'}}>
              Raymond
            </span>
          ) : (
            <span style={{fontFamily:'var(--font-logo)', fontStyle:'italic', fontWeight:800,
              fontSize:28, lineHeight:1, background:'linear-gradient(90deg,#109D7B,#009C96,#0097B3)',
              WebkitBackgroundClip:'text', backgroundClip:'text', color:'transparent'}}>
              puo
            </span>
          )}
        </NavLink>
        <div style={{flex:1}}/>
        {multiCompany && (
          <>
            <div ref={switcherRef} style={{position:'relative'}}>
              <button type="button"
                onClick={()=>{ if (!switcherDisabled) setSwitcherOpen(o=>!o); }}
                aria-haspopup="listbox" aria-expanded={switcherOpen}
                title={switcherDisabled ? 'Nem lehet cégek közt váltani, amíg utalás fut.' : 'Aktív cég váltása'}
                style={{
                  display:'flex', alignItems:'center', gap:8, padding:'4px 8px 4px 6px',
                  borderRadius:10, background:switcherOpen?'var(--bg-hover)':'none', border:'none',
                  cursor: switcherDisabled ? 'default' : 'pointer',
                  opacity: switcherDisabled ? 0.45 : 1,
                  pointerEvents: switcherDisabled ? 'none' : 'auto',
                }}>
                {companyBadge}
                <Truncate title={companyName} style={{maxWidth:140, fontSize:'0.78rem',
                  fontWeight:600, color:'var(--text)'}}>
                  {companyName}
                </Truncate>
                <ChevronDown size={12} style={{color:'var(--text-muted)'}}/>
              </button>
              {switcherOpen && (
                <div role="listbox" style={{position:'absolute', top:'calc(100% + 6px)', right:0, width:280,
                  background:'var(--bg-card)', border:'1px solid var(--border)',
                  borderRadius:'var(--radius-lg)', padding:6, boxShadow:'var(--shadow)', zIndex:50}}>
                  <div style={{padding:'6px 10px 4px', fontSize:'0.68rem', fontWeight:600,
                    color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em'}}>
                    Aktív cégek
                  </div>
                  {liveMemberships.map(m => {
                    const isActive = m.company_id === activeCompanyId;
                    const rowStyle = {
                      display:'flex', alignItems:'center', gap:8, width:'100%',
                      padding:'8px 10px', borderRadius:8, fontSize:'0.82rem',
                      color:'var(--text)', textAlign:'left', border:'none',
                      borderLeft:`2px solid ${isActive ? 'var(--brand, var(--primary))' : 'transparent'}`,
                      background: isActive ? 'var(--primary-subtle, rgba(59,130,246,0.15))' : 'none',
                      cursor: isActive ? 'default' : 'pointer',
                    };
                    const content = (
                      <>
                        <Truncate title={m.company_name} style={{flex:1, fontWeight: isActive ? 600 : 500}}>
                          {m.company_name}
                        </Truncate>
                        <Badge variant="default">{roleLabel(m.role)}</Badge>
                        {isActive && <Check size={14} style={{color:'var(--brand, var(--primary))', flexShrink:0}}/>}
                      </>
                    );
                    return isActive ? (
                      // A jelenleg aktív sor NEM kattintható — nincs mire váltani.
                      <div key={m.company_id} role="option" aria-selected style={rowStyle}>{content}</div>
                    ) : (
                      <button key={m.company_id} type="button" role="option" aria-selected={false}
                        onClick={()=>{ setSwitcherOpen(false); switchCompany(m.company_id, m.company_name); }}
                        onMouseEnter={e=>{ e.currentTarget.style.background='var(--bg-hover)'; }}
                        onMouseLeave={e=>{ e.currentTarget.style.background='none'; }}
                        style={rowStyle}>
                        {content}
                      </button>
                    );
                  })}
                  {expiredMemberships.length > 0 && (
                    <>
                      <div style={{height:1, background:'var(--border)', margin:'4px 0'}}/>
                      <div style={{padding:'6px 10px 4px', fontSize:'0.68rem', fontWeight:600,
                        color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em'}}>
                        Lejárt előfizetés
                      </div>
                      {expiredMemberships.map(m => (
                        <div key={m.company_id} style={{
                          display:'flex', alignItems:'center', gap:8, width:'100%',
                          padding:'8px 10px', borderRadius:8, fontSize:'0.82rem',
                          color:'var(--text)', opacity: m.company_id === activeCompanyId ? 0.85 : 0.55,
                          borderLeft:`2px solid ${m.company_id === activeCompanyId ? 'var(--danger)' : 'transparent'}`,
                        }}>
                          <Truncate title={m.company_name} style={{flex:1}}>{m.company_name}</Truncate>
                          <Badge variant="danger">Lejárt</Badge>
                          <Btn size="sm" variant="ghost"
                            onClick={()=>{ setSwitcherOpen(false); setActivateFor(m); }}>
                            Aktiválás
                          </Btn>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
            <div style={{height:24, width:1, background:'var(--border)'}}/>
          </>
        )}
        {/* M12: „Aktiválás" — Modal (NEM új route), a FeedbackWidgetre mutat. */}
        <Modal open={!!activateFor} onClose={()=>setActivateFor(null)} width={440}
          title="Előfizetés aktiválása">
          {activateFor && (
            <div style={{display:'flex', flexDirection:'column', gap:'0.8rem', fontSize:'0.85rem', lineHeight:1.55}}>
              <div>
                A(z) <b>{activateFor.company_name}</b> előfizetése
                {activateFor.company_is_active === false
                  ? ' inaktív állapotban van.'
                  : activateFor.subscription_expires_at
                    ? <> lejárt ({new Date(activateFor.subscription_expires_at).toLocaleDateString('hu-HU')}).</>
                    : ' lejárt.'}
                {' '}Amíg nem aktív, ezt a céget nem lehet kiválasztani.
              </div>
              <div style={{color:'var(--text-muted)', fontSize:'0.8rem'}}>
                Az aktiválást a rendszergazda végzi el. Jelezd nekünk a visszajelzés-űrlapon,
                és felvesszük veled a kapcsolatot.
              </div>
              <div style={{display:'flex', gap:8, justifyContent:'flex-end'}}>
                <Btn variant="ghost" onClick={()=>setActivateFor(null)}>Mégse</Btn>
                <Btn onClick={()=>openFeedbackFor(activateFor)}>Aktiválás kérése</Btn>
              </div>
            </div>
          )}
        </Modal>
        <div ref={menuRef} style={{position:'relative'}}>
          <button type="button" onClick={()=>setMenuOpen(o=>!o)} style={{
            display:'flex', alignItems:'center', gap:10, padding:'4px 8px 4px 6px',
            borderRadius:10, background:menuOpen?'var(--bg-hover)':'none', border:'none',
            cursor:'pointer',
          }}>
            {!multiCompany && companyBadge}
            <span style={{width:26, height:26, borderRadius:'50%', flexShrink:0,
              background:'#3b82f6', display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:'0.66rem', fontWeight:700, color:'#fff'}}>
              {userInitial}
            </span>
            <ChevronDown size={14} style={{color:'var(--text-muted)'}}/>
          </button>
          {menuOpen && (
            <div style={{position:'absolute', top:'calc(100% + 6px)', right:0, width:220,
              background:'var(--bg-card)', border:'1px solid var(--border)',
              borderRadius:'var(--radius-lg)', padding:6, boxShadow:'var(--shadow)', zIndex:50}}>
              <div style={{padding:'8px 10px', fontSize:'0.72rem', color:'var(--text-muted)',
                overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}} title={user?.email}>
                {user?.email}
              </div>
              <div style={{height:1, background:'var(--border)', margin:'4px 0'}}/>
              <NavLink to="/settings/company" onClick={()=>setMenuOpen(false)} style={{
                display:'flex', alignItems:'center', gap:8, padding:'8px 10px', borderRadius:8,
                fontSize:'0.82rem', color:'var(--text)', textDecoration:'none'}}>
                <Settings size={14}/> Beállítások
              </NavLink>
              <button type="button" onClick={()=>{setMenuOpen(false); logout();}} style={{
                display:'flex', alignItems:'center', gap:8, padding:'8px 10px', width:'100%',
                borderRadius:8, fontSize:'0.82rem', color:'#f1a3a3', background:'none',
                border:'none', cursor:'pointer', textAlign:'left'}}>
                <LogOut size={14}/> Kijelentkezés
              </button>
            </div>
          )}
        </div>
      </header>

      <div style={{display:'flex', flex:1, overflow:'hidden'}}>
      {/* Desktop sidebar */}
      <aside className="sidebar" style={{
        width:210, flexShrink:0, background:'var(--bg-surface)',
        borderRight:'1px solid var(--border)', display:'flex', flexDirection:'column',
        overflow:'hidden',
      }}>
        <nav style={{flex:1,padding:'0.6rem 0.4rem',overflowY:'auto'}}>
          {NAV.map((item) => (
            <NavGroup key={item.to} item={item} open={!!item.sub && inGroup(item)}/>
          ))}
        </nav>

        {/* A felhasználói e-mail + kijelentkezés a felső sáv fiók-lenyílójába
            költözött — itt csak a verziószám + Revolut env-jelző marad. */}
        <div style={{padding:'0.7rem 1rem',borderTop:'1px solid var(--border)',
          display:'flex',alignItems:'center',gap:6}}>
          <span style={{fontSize:'0.62rem',color:'var(--text-dim)'}}>v2.0</span>
          {revEnv && revConnected && (
            <span title="Revolut környezet" style={{marginLeft:'auto',fontSize:'0.6rem',
              fontWeight:800,padding:'1px 7px',borderRadius:6,letterSpacing:'0.04em',
              color:'#fff',background: revLive ? ENV_RED : ENV_GREEN}}>
              {revLive ? 'ÉLES' : 'SANDBOX'}
            </span>
          )}
        </div>
      </aside>

      {/* Main */}
      <main style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden'}}>
        {revLive && (
          <div style={{background:ENV_RED,color:'#fff',fontSize:'0.72rem',fontWeight:700,
            textAlign:'center',padding:'3px 0',letterSpacing:'0.05em',flexShrink:0}}>
            ⚠ ÉLES REVOLUT KÖRNYEZET – a Revolut utalások VALÓDI pénzt mozgatnak
          </div>
        )}
        <div className="main-scroll" style={{flex:1,overflowY:'auto',padding:'1.1rem'}}>
          {children}
        </div>
      </main>
      </div>

      {/* Mobile bottom nav — 4 fix + „Több" (K3) */}
      <nav className="mobile-nav">
        {MOBILE_BAR.map(({ label, to, icon: Icon }) => (
          <NavLink key={to} to={to} end={to==='/'} onClick={()=>setMoreOpen(false)}
            style={({ isActive }) => ({
              display:'flex', flexDirection:'column', alignItems:'center', gap:3,
              padding:'0.4rem 0.5rem', fontSize:'0.58rem', fontWeight:500,
              color: isActive ? 'var(--primary)' : 'var(--text-muted)',
              textDecoration:'none', flex:1,
            })}>
            <Icon size={17}/>
            {label}
          </NavLink>
        ))}
        <button type="button" onClick={()=>setMoreOpen(o=>!o)}
          aria-expanded={moreOpen} aria-label="Több menüpont"
          style={{display:'flex',flexDirection:'column',alignItems:'center',gap:3,
            padding:'0.4rem 0.5rem',fontSize:'0.58rem',fontWeight:500,flex:1,
            background:'none',border:'none',cursor:'pointer',
            color: moreOpen || MOBILE_MORE.some(inGroup)
              ? 'var(--primary)' : 'var(--text-muted)'}}>
          <MoreHorizontal size={17}/>
          Több
        </button>
      </nav>

      {/* A „Több" alsó lapja. Sima fixed réteg — a `Modal` itt rossz lenne: ez nem
          dialógus, hanem navigáció, és a háttér görgetését sem kell zárolni. */}
      {moreOpen && (
        <div className="mobile-more" onClick={e=>{ if (e.target===e.currentTarget) setMoreOpen(false); }}>
          <div className="mobile-more-sheet">
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
              padding:'0.7rem 1rem',borderBottom:'1px solid var(--border-dim)'}}>
              <span style={{fontSize:'0.8rem',fontWeight:700}}>Több</span>
              <button type="button" onClick={()=>setMoreOpen(false)} aria-label="Bezárás"
                style={{background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',padding:4}}>
                <X size={16}/>
              </button>
            </div>
            {MOBILE_MORE.map(({ label, to, icon: Icon }) => (
              <NavLink key={to} to={to} onClick={()=>setMoreOpen(false)}
                style={({ isActive }) => ({
                  display:'flex', alignItems:'center', gap:10, padding:'0.8rem 1rem',
                  fontSize:'0.88rem', textDecoration:'none',
                  borderBottom:'1px solid var(--border-dim)',
                  color: isActive ? 'var(--primary)' : 'var(--text)',
                })}>
                <Icon size={17}/>
                {label}
              </NavLink>
            ))}
          </div>
        </div>
      )}

      {/* 2026-08-02 (admin F4): hibajelentő widget minden bejelentkezett oldalon. */}
      <FeedbackWidget />
      {/* D-WP-06 (2026-08-25): AI chat-widget minden bejelentkezett oldalon —
          a Feedback-gomb fölött, hogy ne fedjék egymást. */}
      <ChatWidget />

      <style>{`
        .sidebar { display: flex !important; }
        .mobile-nav { display: none !important; }
        @media (max-width: 768px) {
          .sidebar { display: none !important; }
          .mobile-nav {
            display: flex !important; position: fixed; bottom: 0; left: 0; right: 0;
            z-index: 100; background: var(--bg-surface);
            border-top: 1px solid var(--border);
            padding: 0.25rem 0 env(safe-area-inset-bottom, 0.25rem);
          }
          .main-scroll { padding-bottom: 5rem !important; }
        }
        .mobile-more { display: none !important; }
        @media (max-width: 768px) {
          .mobile-more {
            display: block !important; position: fixed; inset: 0; z-index: 99;
            background: rgba(4,7,13,0.5);
          }
          .mobile-more-sheet {
            position: absolute; left: 0; right: 0;
            bottom: calc(3.2rem + env(safe-area-inset-bottom, 0.25rem));
            background: var(--bg-card); border-top: 1px solid var(--border);
            border-radius: var(--radius-lg) var(--radius-lg) 0 0; overflow: hidden;
          }
        }
      `}</style>
    </div>
  );
}
